import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Divider, CircularProgress, Tooltip,
} from '@mui/material';
import { CheckCircle, Link as LinkIcon, PlayArrow, Search, Settings, ContentCopy, HelpOutline } from '@mui/icons-material';
import { api } from '../config/api';
import { leerError } from '../utils/errores';

const Paso: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#9333ea', color: '#fff', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

const Copiable: React.FC<{ label: string; valor: string; onCopiar: () => void }> = ({ label, valor, onCopiar }) => (
  <TextField fullWidth size="small" label={label} value={valor} sx={{ mt: 1 }}
    InputProps={{ readOnly: true, endAdornment: <Button size="small" startIcon={<ContentCopy />} onClick={() => { navigator.clipboard.writeText(valor); onCopiar(); }}>Copiar</Button> }} />
);

const WooCommerceCard: React.FC<{ onMensaje: (t: string, sev: 'success' | 'error' | 'info') => void }> = ({ onMensaje }) => {
  const [e, setE] = useState<any>(null);
  const [guia, setGuia] = useState(false);
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [resultado, setResultado] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [errorGuia, setErrorGuia] = useState('');

  const cargar = useCallback(() => {
    api.get('/integraciones/woocommerce').then((r) => {
      setE(r.data.config);
      // Mensaje contextualizado si acaba de conectar
      if (r.data.config?.activo && !window.sessionStorage.getItem('woo_connected_msg_shown')) {
        window.sessionStorage.setItem('woo_connected_msg_shown', 'true');
        setTimeout(() => {
          onMensaje(
            `🎉 ¡Listo! Tu tienda WooCommerce está conectada.\n` +
            `El agente ya puede:\n` +
            `✅ Buscar en tu catálogo (${r.data.config.tool_catalogo ? 'búsqueda activada' : 'desactivada'})\n` +
            `✅ Consultar estado de pedidos (${r.data.config.tool_pedidos ? 'consulta activada' : 'desactivada'})`,
            'success'
          );
        }, 500);
      }
    }).catch(() => setE(null));
  }, [onMensaje]);
  useEffect(cargar, [cargar]);

  const guardarYConectar = async () => {
    setErrorGuia('');
    setOcupado(true);
    try {
      await api.put('/integraciones/woocommerce', { store_url: storeUrl, consumer_key: consumerKey, consumer_secret: consumerSecret });
      setConsumerSecret('');
      setGuia(false);
      setStoreUrl('');
      onMensaje('✅ Credenciales guardadas. WooCommerce está listo para usar.', 'success');
      cargar();
    } catch (err: any) {
      setErrorGuia(leerError(err, 'No se pudieron guardar las credenciales.').message);
    } finally {
      setOcupado(false);
    }
  };

  const cambiarOpcion = async (k: string, v: boolean) => {
    setE({ ...e, [k]: v });
    try { 
      await api.put('/integraciones/woocommerce', { [k]: v });
      
      // Mensajes contextualizados según qué herramienta se activó/desactivó
      const msgs: Record<string, {on: string, off: string}> = {
        tool_catalogo: {
          on: '✅ Búsqueda de Productos activada. El agente buscará en tiempo real tu catálogo.',
          off: '⚠️ Búsqueda de Productos desactivada. El agente no podrá responder sobre tus productos.',
        },
        tool_pedidos: {
          on: '✅ Estado de Pedidos activado. Los clientes pueden consultar el estado de sus compras.',
          off: '⚠️ Estado de Pedidos desactivado. Los clientes no podrán consultar sus pedidos.',
        },
      };
      
      const msg = msgs[k]?.[v ? 'on' : 'off'] || 'Opción actualizada.';
      onMensaje(msg, v ? 'success' : 'info');
    }
    catch { onMensaje('No se pudo guardar la opción.', 'error'); cargar(); }
  };

  const probar = async () => {
    setOcupado(true);
    try {
      const { data } = await api.post('/integraciones/woocommerce/probar');
      onMensaje(data.ok ? `✅ Conectado: ${data.productos} productos activos en tu tienda.` : `Error: ${data.error}`, data.ok ? 'success' : 'error');
    } finally { setOcupado(false); }
  };

  const buscar = async () => {
    if (!busqueda.trim()) return;
    setOcupado(true); setResultado('');
    try { 
      const { data } = await api.post('/integraciones/woocommerce/probar', { tipo: 'producto', criterio: busqueda });
      setResultado(data.resultado); 
    }
    catch (err: any) { setResultado(leerError(err, 'No se pudo buscar.').message); }
    finally { setOcupado(false); }
  };

  const desconectar = async () => {
    await api.delete('/integraciones/woocommerce');
    onMensaje('WooCommerce desconectado. Tus credenciales quedan guardadas por si vuelves a conectar.', 'info');
    cargar();
  };

  const borrarTodo = async () => {
    if (!window.confirm('¿Borrar todas las credenciales de WooCommerce?')) return;
    await api.delete('/integraciones/woocommerce');
    onMensaje('Credenciales de WooCommerce eliminadas.', 'info');
    cargar();
  };

  if (!e) return null;
  const copiado = () => onMensaje('Copiado', 'info');

  return (
    <Box sx={{ mb: 3 }}>
      <Card sx={{ mt: 0.5, border: '1px solid #E2E8F0' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box component="img" src="/logos/woocommerce.png" alt="Logo de WooCommerce" sx={{ height: 34, width: 'auto' }} onError={(e: any) => e.target.style.display = 'none'} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>🛒 WooCommerce</Typography>
              {e.activo && <Chip size="small" color="success" icon={<CheckCircle />} label="Conectado" />}
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Conecta tu tienda WooCommerce para que el agente busque tus productos en tiempo real, consulte el estado de pedidos 
            y responda con información actualizada de disponibilidad y precios. Mantén tu stock sincronizado automáticamente.
          </Typography>

          {/* Beneficios destacados */}
          {e.activo && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#166534', mb: 0.5 }}>
                ✅ Beneficios activos:
              </Typography>
              <Stack spacing={0.5} sx={{ fontSize: 12, color: '#166534' }}>
                <Typography variant="caption">🚀 Respuestas al instante: ~5 segundos</Typography>
                <Typography variant="caption">📦 Stock en vivo: sincronización automática de tu tienda</Typography>
                <Typography variant="caption">💰 +30% conversión: clientes obtienen respuestas exactas</Typography>
                <Typography variant="caption">🔄 Información actualizada: precio y disponibilidad reales</Typography>
              </Stack>
            </Box>
          )}

          {!e.tiene_credenciales && (
            <Button variant="contained" startIcon={<Settings />} onClick={() => setGuia(true)} sx={{ bgcolor: '#9333ea', '&:hover': { bgcolor: '#7e22ce' } }}>
              Configurar WooCommerce
            </Button>
          )}

          {e.tiene_credenciales && !e.activo && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="contained" startIcon={ocupado ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />} onClick={probar} disabled={ocupado} sx={{ bgcolor: '#9333ea', '&:hover': { bgcolor: '#7e22ce' } }}>
                Conectar Tienda
              </Button>
              <Button onClick={() => { setStoreUrl(e.store_url || ''); setGuia(true); }}>Editar credenciales</Button>
              <Button color="error" onClick={borrarTodo}>Borrar</Button>
            </Stack>
          )}

          {e.activo && (
            <Box>
              <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                <Box>
                  <FormControlLabel 
                    control={<Switch checked={!!e.tool_catalogo} onChange={(ev) => cambiarOpcion('tool_catalogo', ev.target.checked)} />} 
                    label={
                      <Stack sx={{ ml: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>🛍️ Búsqueda de Productos</Typography>
                        <Typography variant="caption" color="text.secondary">El agente busca tus productos por nombre y devuelve precio, stock y link</Typography>
                      </Stack>
                    }
                  />
                </Box>
                <Box sx={{ ml: 4, mb: 0.5 }}>
                  <Tooltip title="Ejemplo: Cliente pregunta 'tienes zapatillas deportivas negras?' → agente busca en tu catálogo y responde al instante">
                    <HelpOutline sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                  </Tooltip>
                </Box>

                <Box>
                  <FormControlLabel 
                    control={<Switch checked={!!e.tool_pedidos} onChange={(ev) => cambiarOpcion('tool_pedidos', ev.target.checked)} />} 
                    label={
                      <Stack sx={{ ml: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>📦 Estado de Pedidos</Typography>
                        <Typography variant="caption" color="text.secondary">Consulta el estado, total y productos de cualquier pedido</Typography>
                      </Stack>
                    }
                  />
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Tooltip title="El cliente da el número o email de la compra, el agente devuelve estado completo: pendiente, procesando, enviado, etc.">
                    <HelpOutline sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                <TextField size="small" label="Probar búsqueda" placeholder="Ej: zapatillas negras" value={busqueda} onChange={(ev) => setBusqueda(ev.target.value)} onKeyDown={(ev) => ev.key === 'Enter' && buscar()} sx={{ flex: 1 }} />
                <Button variant="outlined" startIcon={<Search />} onClick={buscar} disabled={ocupado || !busqueda.trim()}>Buscar</Button>
              </Stack>
              {resultado && <Alert severity="info" sx={{ mb: 1, whiteSpace: 'pre-line' }}>{resultado}</Alert>}
              {e.activo && (
                <Alert severity="info" sx={{ mt: 1.5, mb: 1.5 }}>
                  <strong>ℹ️ WooCommerce (modo lectura).</strong> Tu agente puede consultar pedidos 
                  y buscar productos cuando el cliente pregunta con el nombre.
                  <br />Para recibir <strong>notificaciones automáticas</strong> de nuevos pedidos, 
                  configura un webhook (ej: Zapier + WhatsApp) que llame a tRespondo cuando haya compra.
                </Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" startIcon={<PlayArrow />} onClick={probar} disabled={ocupado}>Probar conexión</Button>
                <Button size="small" onClick={() => { setStoreUrl(e.store_url || ''); setGuia(true); }}>Ver configuración</Button>
                <Button size="small" color="error" onClick={desconectar}>Desconectar</Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Guía paso a paso */}
      <Dialog open={guia} onClose={() => setGuia(false)} fullWidth maxWidth="sm">
        <DialogTitle>Conectar tu Tienda WooCommerce</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Necesitarás la URL de tu tienda y un par de credenciales API. Toma 2 minutos.
          </Typography>
          <Paso n={1}>
            <Typography variant="body2">
              Entra al admin de tu tienda en <strong>tunegocio.cl/wp-admin</strong>
            </Typography>
          </Paso>
          <Paso n={2}>
            <Typography variant="body2">
              Ve a <strong>Configuración → API de REST</strong> y crea una nueva credencial API con acceso de lectura.
            </Typography>
          </Paso>
          <Paso n={3}>
            <Typography variant="body2">
              WooCommerce te mostrará un <strong>Consumer Key</strong> y <strong>Consumer Secret</strong>: cópialos aquí.
            </Typography>
          </Paso>
          <Divider sx={{ my: 2 }} />
          {errorGuia && <Alert severity="error" sx={{ mb: 2 }}>{errorGuia}</Alert>}
          <TextField fullWidth label="URL de tu tienda" placeholder="https://mitienda.cl" value={storeUrl} onChange={(ev) => setStoreUrl(ev.target.value.trim())} sx={{ mb: 2 }} />
          <TextField fullWidth label="Consumer Key" value={consumerKey} onChange={(ev) => setConsumerKey(ev.target.value.trim())} sx={{ mb: 2 }} placeholder="Ej: ck_xxxxxxxxxxxxx" />
          <TextField fullWidth label="Consumer Secret" type="password" value={consumerSecret} onChange={(ev) => setConsumerSecret(ev.target.value.trim())}
            helperText={e.tiene_credenciales ? 'Por seguridad no mostramos tu Consumer Secret guardado. Vuelve a pegarlo para cambiar.' : 'Se guarda cifrado y nunca se muestra.'} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGuia(false)}>Cancelar</Button>
          <Button variant="contained" disabled={ocupado || !storeUrl || !consumerKey || !consumerSecret} onClick={guardarYConectar} sx={{ bgcolor: '#9333ea', '&:hover': { bgcolor: '#7e22ce' } }}>
            {ocupado ? 'Guardando...' : 'Guardar y Conectar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WooCommerceCard;
